/**
 * HelpWidget — floating Help / Knowledge launcher (brand-consistent, mirrors
 * the ERIO help surface for parity).
 *
 * A fixed bottom-right "?" button opens a brand panel with: a quick FAQ
 * (filterable), a link to the full /help page, and a Methodology PDF download.
 * Hidden on /admin routes.
 */
import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { HelpCircle, X, Search, BookOpen, FileDown, ArrowRight } from "lucide-react";

const FAQ = [
  { q: "What's the difference between a lite and a full assessment?", a: "The lite self-assessment is a free, anonymous 5-minute maturity snapshot. A full engagement is a facilitated diagnostic with evidence review, scoring and a board-grade improvement plan." },
  { q: "How is my maturity score calculated?", a: "Deterministically: weighted indicator scores roll up per dimension, with dealbreaker caps and a completeness measure. The engine — not an LLM — produces the number, so it's defensible." },
  { q: "How is pricing worked out?", a: "From an admin-editable rate card (day-rates × utilisation × region/sector/size modifiers). Every figure in an engagement plan can be defended to procurement." },
  { q: "What are the 12 sub-brands?", a: "EKSELENS360 spans AI360, DIGITAL360, PMO360, CHANGE360, OPERATING360, SUPPLIER360, ITSM360, DATA360, BENEFITS360, GOVERN360, CAPABILITY360 and STAKEHOLDER360." },
  { q: "How do you handle my data?", a: "Anonymous lite data is keyed to a random device id with self-service erasure on /privacy. Identified submissions follow our published retention policy." },
];

export default function HelpWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(null);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  if ((location.pathname || "").startsWith("/admin")) return null;

  const filtered = FAQ.filter(
    (f) => !query || (f.q + " " + f.a).toLowerCase().includes(query.toLowerCase())
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-5 right-5 z-[70] w-12 h-12 rounded-full bg-[#7737F6] text-white shadow-lg flex items-center justify-center hover:bg-[#6322e0] transition-colors"
        aria-label="Help & knowledge"
        data-testid="help-widget-launcher"
      >
        {open ? <X size={20} /> : <HelpCircle size={22} />}
      </button>

      {open && (
        <div
          className="fixed bottom-20 right-5 z-[70] w-[min(92vw,380px)] bg-white border border-zinc-200 shadow-2xl"
          role="dialog"
          aria-label="Help and knowledge centre"
          data-testid="help-widget-panel"
        >
          <div className="bg-black text-white p-5">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#C7B0FE] font-semibold">Help &amp; knowledge</div>
            <h3 className="ek-heading text-lg mt-1">How can we help?</h3>
            <div className="mt-3 flex items-center gap-2 bg-white/10 px-3 border border-white/15">
              <Search size={14} className="text-white/60" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the FAQ…"
                className="flex-1 bg-transparent py-2.5 text-[13px] text-white placeholder:text-white/40 outline-none"
                data-testid="help-widget-search"
              />
            </div>
          </div>

          <div className="max-h-[42vh] overflow-y-auto divide-y divide-zinc-100" data-testid="help-widget-faq">
            {filtered.length === 0 ? (
              <p className="p-4 text-[13px] text-zinc-500">No matches. Try the full help centre below.</p>
            ) : (
              filtered.map((f, i) => (
                <div key={f.q} className="px-4">
                  <button
                    type="button"
                    onClick={() => setExpanded(expanded === i ? null : i)}
                    className="w-full text-left py-3 text-[13.5px] font-medium text-black hover:text-[#7737F6]"
                    data-testid="help-widget-faq-item"
                  >
                    {f.q}
                  </button>
                  {expanded === i && (
                    <p className="pb-3 text-[12.5px] text-zinc-600 leading-relaxed">{f.a}</p>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="border-t border-zinc-200 p-3 space-y-1.5">
            <Link
              to="/help"
              className="flex items-center gap-2 px-2 py-2 text-[13px] text-black hover:bg-[#F2F2F3]"
              data-testid="help-widget-full-help"
            >
              <BookOpen size={15} className="text-[#7737F6]" /> Full help &amp; methodology
              <ArrowRight size={13} className="ml-auto text-zinc-300" />
            </Link>
            <Link
              to="/help"
              className="flex items-center gap-2 px-2 py-2 text-[13px] text-black hover:bg-[#F2F2F3]"
              data-testid="help-widget-methodology-request"
            >
              <FileDown size={15} className="text-[#7737F6]" /> Request the methodology paper
              <ArrowRight size={13} className="ml-auto text-zinc-300" />
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
